/**
 * @fileoverview Edit global staff-phone ring-duration settings through the backend phone boundary and host the blocked-caller list (`BlockedCallers.js`). Accepted configuration and unsaved form text stay separate. `People.js` owns per-person phone/lifecycle context; `kc-internal-backend/functions/call_routing.py` owns routing operations. This editor represents configuration acceptance rather than proof that a live call used the setting.
 */

import React, { useContext, useEffect, useState } from 'react';

import api from '../../api';
import ToastContext from '../../ToastContext';
import { AppButton, AppIcon, AppSpinner, AppTextInput } from '../shared';
import { AdminCard, AdminSection } from './AdminChrome';
import BlockedCallers from './BlockedCallers';
import './PhoneAdmin.css';



function PhoneAdmin() {
  const showToast = useContext(ToastContext);
  const [settings, setSettings] = useState(null);
  const [ringSeconds, setRingSeconds] = useState('30');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    api.get('/admin/phone-settings')
      .then(({ data }) => {
        if (!active) return;
        setSettings(data);
        setRingSeconds(String(data.ring_seconds));
      })
      .catch((error) => {
        console.error(error);
        showToast({ severity: 'error', summary: 'Error', detail: 'Phone settings could not be loaded' });
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [showToast]);

  const save =

  async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/admin/phone-settings', { ring_seconds: Number(ringSeconds) });
      setSettings(data);
      setRingSeconds(String(data.ring_seconds));
      showToast({ severity: 'success', summary: 'Saved', detail: 'Firm ring time queued for every extension' });
    } catch (error) {
      showToast({ severity: 'error', summary: 'Could not save', detail: error?.response?.data?.error || 'Phone settings could not be updated' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="phone-admin-loading"><AppSpinner /></div>;
  }

  const minimum = settings?.min_ring_seconds ?? 10;
  const maximum = settings?.max_ring_seconds ?? 120;
  const numericValue = Number(ringSeconds);
  const valid = Number.isInteger(numericValue) && numericValue >= minimum && numericValue <= maximum;

  return (
    <AdminSection
      title="Phone"
      description="Set firm-wide call behavior. Individual extensions and direct lines are assigned in People.">
      <div className="phone-admin-grid">
        <AdminCard title="Call behavior" description="This ring time applies to every enabled staff extension.">
          <div className="phone-admin-setting">
            <span className="phone-admin-setting__icon" aria-hidden="true"><AppIcon icon="pi pi-clock" /></span>
            <label htmlFor="firm-ring-seconds">
              <strong>Ring time</strong>
              <span>How long a staff phone rings before its unavailable route takes over.</span>
            </label>
            <div className="phone-admin-setting__control">
              <AppTextInput
                id="firm-ring-seconds"
                type="number"
                min={minimum}
                max={maximum}
                aria-label="Ring time"
                value={ringSeconds}
                onChange={(event) => setRingSeconds(event.target.value)} />
              <span>seconds</span>
            </div>
          </div>
          <div className="phone-admin-actions">
            <AppButton
              icon="pi pi-save"
              label={saving ? 'Saving' : 'Save ring time'}
              disabled={saving || !valid || numericValue === settings?.ring_seconds}
              onClick={save} />
          </div>
        </AdminCard>
        <BlockedCallers />
      </div>
    </AdminSection>
  );
}

export default PhoneAdmin;
